import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';

(() => {
  const banner = document.querySelector('[data-ib-diwali-floating]');
  if (!banner || banner.dataset.ibGiftReady === 'true') return;
  banner.dataset.ibGiftReady = 'true';

  const minimiseButton = banner.querySelector('[data-ib-diwali-dismiss]');
  const reopenButton = banner.querySelector('[data-ib-diwali-reopen]');
  const panel = banner.querySelector('[data-ib-diwali-panel]');
  const copyButton = banner.querySelector('[data-ib-diwali-copy]');
  const applyButton = banner.querySelector('[data-ib-diwali-apply]');
  const status = banner.querySelector('[data-ib-diwali-status]');
  const code = 'INNOV8';
  const stateKey = 'ib-diwali-offer-minimised-v4';
  const applyLabel = 'Apply coupon to cart';
  const autoMinimiseDelay = 15000;
  const transitionDuration = 440;
  let autoMinimiseTimer = null;
  let reopenHideTimer = null;
  let cardHeightAnimation = null;

  const scheduleAutoMinimise = () => {
    window.clearTimeout(autoMinimiseTimer);
    if (banner.classList.contains('is-collapsed')) return;
    autoMinimiseTimer = window.setTimeout(() => {
      const focusIsInside = banner.contains(document.activeElement);
      setMinimised(true, focusIsInside);
    }, autoMinimiseDelay);
  };

  const animateCardHeight = (fromHeight, toHeight) => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || Math.abs(fromHeight - toHeight) < 1) {
      banner.style.height = '';
      return;
    }

    const animation = banner.animate(
      [{ height: `${fromHeight}px` }, { height: `${toHeight}px` }],
      {
        duration: transitionDuration,
        easing: 'cubic-bezier(.22, 1, .36, 1)',
        fill: 'forwards'
      }
    );
    cardHeightAnimation = animation;

    animation.onfinish = () => {
      if (cardHeightAnimation !== animation) return;
      banner.style.height = '';
      cardHeightAnimation = null;
      animation.cancel();
    };
    animation.oncancel = () => {
      if (cardHeightAnimation === animation) cardHeightAnimation = null;
    };
  };

  const setMinimised = (minimised, moveFocus = false, animate = true) => {
    window.clearTimeout(autoMinimiseTimer);
    window.clearTimeout(reopenHideTimer);

    // Capture the current animated height before cancelling an in-flight transition.
    const currentHeight = banner.getBoundingClientRect().height;
    if (cardHeightAnimation) {
      cardHeightAnimation.cancel();
      cardHeightAnimation = null;
    }
    banner.style.height = `${currentHeight}px`;

    if (reopenButton) {
      reopenButton.hidden = false;
      reopenButton.setAttribute('aria-hidden', minimised ? 'false' : 'true');
      reopenButton.tabIndex = minimised ? 0 : -1;
    }
    if (minimiseButton) {
      minimiseButton.hidden = minimised;
      minimiseButton.setAttribute('aria-hidden', minimised ? 'true' : 'false');
      minimiseButton.tabIndex = minimised ? -1 : 0;
    }

    banner.classList.toggle('is-collapsed', minimised);
    banner.setAttribute('data-ib-diwali-state', minimised ? 'minimised' : 'expanded');
    if (panel) panel.inert = minimised;

    let targetHeight = currentHeight;
    if (minimised) {
      targetHeight = Number.parseFloat(
        getComputedStyle(banner).getPropertyValue('--ib-diwali-collapsed-height')
      ) || 60;
    } else {
      // Measure the natural expanded height with the expanded class applied.
      banner.style.height = 'auto';
      targetHeight = banner.getBoundingClientRect().height;
      banner.style.height = `${currentHeight}px`;
    }

    if (animate) {
      void banner.offsetHeight;
      animateCardHeight(currentHeight, targetHeight);
    } else {
      banner.style.height = '';
    }

    if (!minimised && reopenButton) {
      reopenHideTimer = window.setTimeout(() => {
        if (!banner.classList.contains('is-collapsed')) reopenButton.hidden = true;
      }, transitionDuration + 60);
    }

    try {
      if (minimised) window.sessionStorage.setItem(stateKey, '1');
      else window.sessionStorage.removeItem(stateKey);
    } catch (_error) {
      // The widget remains usable when browser storage is unavailable.
    }

    if (moveFocus) (minimised ? reopenButton : minimiseButton)?.focus();
    if (!minimised) scheduleAutoMinimise();
  };

  let startMinimised = false;
  try {
    startMinimised = window.sessionStorage.getItem(stateKey) === '1';
  } catch (_error) {
    // A fresh session starts with the expanded offer.
  }
  setMinimised(startMinimised, false, false);
  minimiseButton?.addEventListener('click', () => setMinimised(true, true));
  reopenButton?.addEventListener('click', () => setMinimised(false, true));

  const showStatus = (message, kind = 'info') => {
    if (!status) return;
    status.textContent = message;
    status.dataset.state = kind;
  };

  const applyCouponToCart = async () => {
    if (!(applyButton instanceof HTMLButtonElement) || applyButton.disabled) return;

    scheduleAutoMinimise();
    applyButton.disabled = true;
    applyButton.textContent = 'Applying coupon…';
    applyButton.setAttribute('aria-busy', 'true');
    showStatus('Applying coupon INNOV8…');

    try {
      const root = window.Shopify?.routes?.root || '/';
      const cartResponse = await fetch(root + 'cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!cartResponse.ok) throw new Error('We could not read your cart. Please try again.');

      const cart = await cartResponse.json();
      if (!Number(cart.item_count)) {
        showStatus('Add a book to your cart before applying coupon INNOV8.', 'error');
        applyButton.textContent = applyLabel;
        return;
      }

      const discountCodes = Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
      const existingAppliedCodes = discountCodes
        .filter((discount) => discount && discount.applicable === true && typeof discount.code === 'string')
        .map((discount) => discount.code)
        .filter((existingCode) => existingCode.toUpperCase() !== code);

      const alreadyApplied = discountCodes.some((discount) =>
        discount &&
        typeof discount.code === 'string' &&
        discount.code.toUpperCase() === code &&
        discount.applicable === true
      );

      if (alreadyApplied) {
        showStatus('Coupon INNOV8 is already applied to your cart.', 'success');
        applyButton.textContent = 'Coupon applied ✓';
        scheduleAutoMinimise();
        return;
      }

      const sectionId = document.querySelector('cart-items-component[data-section-id]')?.dataset.sectionId;
      const payload = { discount: [...existingAppliedCodes, code].join(',') };
      if (sectionId) payload.sections = [sectionId];

      const updateResponse = await fetch(root + 'cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const updatedCart = await updateResponse.json();
      if (!updateResponse.ok) {
        throw new Error(updatedCart.description || updatedCart.message || 'Shopify could not update your cart.');
      }

      const requestedCode = Array.isArray(updatedCart.discount_codes)
        ? updatedCart.discount_codes.find((discount) =>
            discount && typeof discount.code === 'string' && discount.code.toUpperCase() === code
          )
        : null;

      if (requestedCode && requestedCode.applicable === false) {
        if (sectionId && updatedCart.sections?.[sectionId]) {
          await morphSection(sectionId, updatedCart.sections[sectionId]);
        }
        showStatus('Coupon INNOV8 is not applicable to the items currently in your cart. You can copy the code and use it at checkout if eligible.', 'error');
        applyButton.textContent = applyLabel;
        scheduleAutoMinimise();
        return;
      }

      if (sectionId && updatedCart.sections?.[sectionId]) {
        await morphSection(sectionId, updatedCart.sections[sectionId]);
      }

      document.dispatchEvent(new DiscountUpdateEvent(updatedCart, 'diwali-offer-banner'));
      showStatus('Coupon INNOV8 has been applied to your cart.', 'success');
      applyButton.textContent = 'Coupon applied ✓';
      scheduleAutoMinimise();
    } catch (error) {
      showStatus(error instanceof Error ? error.message : 'We could not apply the coupon. Please try again.', 'error');
      applyButton.textContent = applyLabel;
      scheduleAutoMinimise();
    } finally {
      applyButton.disabled = false;
      applyButton.removeAttribute('aria-busy');
    }
  };

  applyButton?.addEventListener('click', applyCouponToCart);

  const fallbackCopy = (value) => {
    const field = document.createElement('textarea');
    field.value = value;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.opacity = '0';
    field.style.pointerEvents = 'none';
    document.body.appendChild(field);
    field.select();
    let copied = false;
    try {
      copied = document.execCommand('copy');
    } finally {
      field.remove();
    }
    return copied;
  };

  copyButton?.addEventListener('click', async () => {
    scheduleAutoMinimise();
    let copied = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
        copied = true;
      } else {
        copied = fallbackCopy(code);
      }
    } catch (_error) {
      copied = fallbackCopy(code);
    }

    showStatus(
      copied ? 'Coupon code INNOV8 copied. Use it during checkout for 5% off.' : 'Enter coupon INNOV8 at checkout for 5% off.',
      'info'
    );
    copyButton.textContent = copied ? 'Copied!' : 'Copy INNOV8';
    copyButton.setAttribute('aria-label', copied ? 'Coupon code INNOV8 copied' : 'Copy coupon code INNOV8');
    window.setTimeout(() => {
      if (!copyButton.isConnected) return;
      copyButton.textContent = 'Copy code';
      copyButton.setAttribute('aria-label', 'Copy coupon code INNOV8');
    }, 2600);
  });
})();